import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, Clock, Flame, MapPin, Package, Sparkles, Building2, Truck, Layers } from "lucide-react";
import api from "@/lib/api";

const RECENT_KEY = "ehi_recent_searches";
const loadRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const pushRecent = (q) => {
  if (!q || q.length < 2) return;
  try {
    const cur = loadRecent().filter((r) => r.toLowerCase() !== q.toLowerCase());
    localStorage.setItem(RECENT_KEY, JSON.stringify([q, ...cur].slice(0, 8)));
  } catch { /* ignore */ }
};

const GROUP_ICON = {
  equipment: Package, cities: MapPin, categories: Layers,
  suppliers: Building2, services: Truck, popular: Flame,
};

export default function GlobalSearchBar({ big = false }) {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [recent, setRecent] = useState(loadRecent);
  const [ac, setAc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debRef = useRef(null);
  const nav = useNavigate();

  // Debounced autocomplete fetch
  useEffect(() => {
    if (!focused) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        setBusy(true);
        const { data } = await api.get(`/search/autocomplete?q=${encodeURIComponent(q)}`);
        setAc(data);
      } catch { /* ignore */ }
      finally { setBusy(false); }
    }, 120);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [q, focused]);

  useEffect(() => {
    if (!focused) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [focused]);

  const flatSuggestions = useMemo(() => {
    if (!ac?.groups) return [];
    return ac.groups.flatMap((g) => g.items.map((it) => ({ ...it, _group: g.key, _title: g.title })));
  }, [ac]);

  const goQuery = (query) => {
    const s = (query || q).trim();
    if (s.length < 2) return;
    pushRecent(s); setRecent(loadRecent()); setFocused(false);
    nav(`/search?q=${encodeURIComponent(s)}`);
  };

  const selectItem = (item) => {
    setFocused(false); setActiveIdx(-1);
    if (item.kind === "category" || item.kind === "equipment") {
      if (item.id) return nav(`/marketplace?category=${item.id}`);
      return goQuery(item.label);
    }
    if (item.kind === "city") return nav(`/marketplace?city=${encodeURIComponent(item.label)}`);
    if (item.kind === "supplier" || item.kind === "service_provider" || item.kind === "vendor") {
      return goQuery(item.label);
    }
    if (item.kind === "service") return goQuery(item.label);
    return goQuery(item.label);
  };

  const onKey = (e) => {
    if (!focused) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, flatSuggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter" && activeIdx >= 0 && flatSuggestions[activeIdx]) {
      e.preventDefault();
      selectItem(flatSuggestions[activeIdx]);
    }
  };

  const clearRecent = () => { try { localStorage.removeItem(RECENT_KEY); } catch {/* ignore */} setRecent([]); };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="global-search">
      <form onSubmit={(e) => { e.preventDefault(); goQuery(); }} className="relative">
        <SearchIcon className={`absolute ${big ? "left-5 top-1/2 -translate-y-1/2 w-6 h-6" : "left-3 top-1/2 -translate-y-1/2 w-4 h-4"} text-slate-400 pointer-events-none`} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setActiveIdx(-1); }}
          onKeyDown={onKey}
          onFocus={() => setFocused(true)}
          placeholder={big ? "Search Equipment, Operators, Inspection Services, Suppliers, Cities…" : "Search equipment, suppliers, spares…"}
          className={`w-full ${big ? "pl-14 pr-32 h-14 text-lg rounded-xl shadow-lg" : "pl-9 pr-24 h-11 text-sm rounded-md"} border border-slate-200 bg-white focus:outline-none focus:border-ehi-primary`}
          data-testid="search-input"
          autoComplete="off"
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); setAc(null); inputRef.current?.focus(); }} className={`absolute ${big ? "right-24 top-1/2 -translate-y-1/2" : "right-20 top-1/2 -translate-y-1/2"} p-1 text-slate-400 hover:text-slate-700`} aria-label="Clear search">
            <X className="w-4 h-4" />
          </button>
        )}
        <button type="submit" className={`absolute right-2 top-1/2 -translate-y-1/2 ${big ? "h-11 px-6 text-base" : "h-9 px-3 text-xs"} bg-ehi-accent hover:bg-ehi-accent/90 text-white font-bold rounded-full`} data-testid="search-submit">
          Search
        </button>
      </form>

      {focused && (
        <div className="absolute z-30 mt-2 w-full bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden max-h-[520px] overflow-y-auto" data-testid="search-dropdown">
          {/* Parsed intent chip strip */}
          {ac?.parsed && (ac.parsed.equipment || ac.parsed.capacity_label || ac.parsed.location) && (
            <div className="px-4 py-2 border-b border-slate-100 flex flex-wrap gap-1.5 items-center bg-slate-50">
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><Sparkles className="w-3 h-3 text-ehi-accent" /> AI understood</span>
              {ac.parsed.equipment && <Chip>{ac.parsed.equipment}</Chip>}
              {ac.parsed.capacity_label && <Chip>{ac.parsed.capacity_label}</Chip>}
              {ac.parsed.location && <Chip><MapPin className="w-3 h-3 inline mr-0.5" />{ac.parsed.location}</Chip>}
              {ac.parsed.intent && <Chip tone="accent">{ac.parsed.intent}</Chip>}
            </div>
          )}

          {/* Grouped suggestions */}
          {(ac?.groups || []).map((g) => {
            const GroupIcon = GROUP_ICON[g.key] || SearchIcon;
            return (
              <div key={g.key} className="p-3 border-b border-slate-100 last:border-b-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 flex items-center gap-1">
                  <GroupIcon className="w-3 h-3 text-ehi-accent" /> {g.title}
                </div>
                <div className="space-y-0.5">
                  {g.items.map((it, i) => {
                    const idx = flatSuggestions.findIndex((f) => f === it || (f.label === it.label && f._group === g.key));
                    const isActive = idx === activeIdx;
                    return (
                      <button
                        key={`${g.key}-${i}-${it.label}`}
                        type="button"
                        onMouseDown={() => selectItem(it)}
                        onMouseEnter={() => setActiveIdx(idx)}
                        className={`w-full flex items-center justify-between gap-2 px-2 py-2 rounded-md text-left min-h-[40px] transition-colors ${isActive ? "bg-ehi-accent/10 text-ehi-accent" : "hover:bg-slate-50 text-slate-800"}`}
                        data-testid={`search-suggestion-${g.key}-${i}`}
                      >
                        <span className="text-sm truncate flex-1">{it.label}</span>
                        {it.sub && <span className="text-[10px] text-slate-500 shrink-0">{it.sub}</span>}
                        {it.city && <span className="text-[10px] text-slate-500 shrink-0">{it.city}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Recent — only when empty query */}
          {q.length < 2 && recent.length > 0 && (
            <div className="p-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Recent</div>
                <button type="button" onMouseDown={clearRecent} className="text-[10px] font-semibold text-slate-500 hover:text-ehi-primary">Clear</button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {recent.map((r) => (
                  <button key={r} type="button" onMouseDown={() => goQuery(r)} className="text-xs bg-slate-100 hover:bg-ehi-primary hover:text-white px-3 py-2 min-h-[36px] rounded-full font-medium transition-colors">
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}
          {busy && <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">Searching…</div>}
        </div>
      )}
    </div>
  );
}

function Chip({ children, tone = "neutral" }) {
  const cls = tone === "accent"
    ? "bg-ehi-accent text-white"
    : "bg-white border border-slate-200 text-slate-700";
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${cls}`}>{children}</span>;
}
